import React from 'react';
import { Link } from 'react-router-dom';
import { buildUrlWithParams } from '../../utils.js';
import { backgroundOverlay, backgroundStyle, backgroundValue } from '../utils/presentation';

function groupCards(cards = [], attribute) {
  if (!attribute || attribute === 'none') return { 'All Cards': [...cards] };
  return cards.reduce((groups, card) => {
    let key = card[attribute] ?? 'Unknown';
    if (typeof key === 'string') key = key.charAt(0).toUpperCase() + key.slice(1);
    if (attribute === 'cost') key = `${key} Mana`;
    if (attribute === 'ttier') key = `Tavern Tier ${key}`;
    return { ...groups, [key]: [...(groups[key] || []), card] };
  }, {});
}

function sortedGroupKeys(groups, attribute, classRenames) {
  const rarityOrder = ['Free', 'Common', 'Rare', 'Epic', 'Legendary'];
  return Object.keys(groups).sort((a, b) => {
    if (attribute === 'rarity') return rarityOrder.indexOf(a) - rarityOrder.indexOf(b);
    if (attribute === 'cost' || attribute === 'ttier') return Number(a.replace(/\D/g, '')) - Number(b.replace(/\D/g, ''));
    if (attribute === 'class') {
      const aIsNeutral = a.trim().toLowerCase() === 'neutral';
      const bIsNeutral = b.trim().toLowerCase() === 'neutral';
      if (aIsNeutral !== bIsNeutral) return aIsNeutral ? 1 : -1;
    }
    const aName = classRenames[a.toLowerCase()]?.trim() || a;
    const bName = classRenames[b.toLowerCase()]?.trim() || b;
    return aName.localeCompare(bName);
  });
}

function sortCards(cards, sortBy) {
  const rarityOrder = ['free', 'common', 'rare', 'epic', 'legendary'];
  return [...cards].sort((a, b) => {
    if (sortBy === 'rarity') {
      const rarityDifference = rarityOrder.indexOf(a.rarity?.toLowerCase()) - rarityOrder.indexOf(b.rarity?.toLowerCase());
      if (rarityDifference !== 0) return rarityDifference;
      return Number(a.cost ?? Infinity) - Number(b.cost ?? Infinity);
    }
    if (sortBy === 'cost' || sortBy === 'ttier') return Number(a[sortBy] ?? Infinity) - Number(b[sortBy] ?? Infinity);
    return String(a[sortBy] || '').localeCompare(String(b[sortBy] || ''), undefined, { sensitivity: 'base' });
  });
}

export default function MainCardsShowcaseModule({ module }) {
  const data = module.data || {};
  const { title = '', intro = '', cards = [], splitby = 'class', sortby = 'cost', columns = '6', mobileColumns = '3', showNames = false, classRenames = {} } = data;
  if (!cards.length) return null;
  const groups = groupCards(cards, splitby);
  const keys = sortedGroupKeys(groups, splitby, classRenames);
  const desktopColumns = Math.min(6, Math.max(4, Number(columns) || 6));
  const style = {
    ...backgroundStyle(backgroundValue(data), { size: data.backgroundSize || 'cover', position: data.backgroundPosition || 'center top', tileSize: data.backgroundTileSize }),
    backgroundColor: data.backgroundColor || '#ead8a5',
    color: data.textColor || '#4f321c',
    '--section-overlay': backgroundOverlay(data, 0),
    '--gallery-columns': desktopColumns,
    '--gallery-card-max': `${230 * 6 / desktopColumns}px`,
    '--gallery-mobile-columns': Math.min(3, Math.max(2, Number(mobileColumns) || 3)),
  };
  return <section className="set-presentation-section set-card-gallery" style={style}><div className="set-presentation-shade" /><div className="container-fluid set-card-gallery-inner">{(title || intro) && <header className="set-card-gallery-intro">{title && <h2>{title}</h2>}{intro && <p>{intro}</p>}</header>}{keys.map((key) => { const display = classRenames[key.toLowerCase()]?.trim() || key; return <section className="set-card-gallery-group" key={key}>{splitby !== 'none' && <div className="set-card-gallery-heading"><span>{display}</span></div>}<div className="set-card-gallery-grid">{sortCards(groups[key], sortby).map((card) => <Link className="set-gallery-card" key={card.id || card.cardid} to={buildUrlWithParams({ ...Object.fromEntries(new URLSearchParams(window.location.search)), selectedCard: card.id })} preventScrollReset><img src={`https://www.hearthcards.net/cards/${card.cardid}.webp`} alt={card.cardname || 'Hearthstone Card'} />{showNames && <span>{card.cardname}</span>}</Link>)}</div></section>; })}</div></section>;
}
